// AGC: SorteioEntryModal - shown after coupon redemption when hasSorteio is true
// AGC: Informs user they are now participating in the raffle and prompts them to complete quest tasks

import React from 'react'
import { X, Trophy, CheckCircle, Instagram, ArrowRight } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import type { SorteioEntryModalData } from '../../types/sorteio'

interface SorteioEntryModalProps {
  data: SorteioEntryModalData
  onClose: () => void
  onGoToTasks: () => void
}

// AGC: Calculate days remaining from endDate to today
function getDaysLeft(endDate: string): number {
  const end = new Date(endDate)
  const now = new Date()
  const diff = end.getTime() - now.getTime()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

export const SorteioEntryModal: React.FC<SorteioEntryModalProps> = ({
  data,
  onClose,
  onGoToTasks,
}) => {
  const { t, formatCurrency } = useTranslation()
  const daysLeft = getDaysLeft(data.endDate)

  return (
    // AGC: Dark overlay with high z-index to sit above all other content
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 overflow-y-auto"
      style={{
        paddingTop: 'max(env(safe-area-inset-top), 1rem)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 1rem)',
      }}
    >
      {/* AGC: Modal card — scrollable with max height */}
      <div className="relative w-full max-w-md rounded-2xl bg-gray-900 text-white shadow-2xl my-auto">

        {/* AGC: Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
          aria-label={t('common.close')}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6">
          {/* AGC: Trophy icon in yellow circle */}
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500/20">
              <Trophy className="h-8 w-8 text-yellow-400" />
            </div>
          </div>

          {/* AGC: Title */}
          <h2 className="mb-2 text-center text-xl font-bold text-yellow-400">
            {t('sorteio.modal.entryTitle')}
          </h2>

          {/* AGC: Prize name */}
          <p className="mb-1 text-center text-base font-semibold text-white">
            {t('sorteio.modal.entryPrize', { prize: data.prize })}
          </p>

          {/* AGC: Optional prize description */}
          {data.prizeDescription && (
            <p className="mb-4 text-center text-sm text-gray-400">
              {data.prizeDescription}
            </p>
          )}

          {/* AGC: Prize image */}
          {data.prizeImageUrl && (
            <img src={data.prizeImageUrl} alt={data.prize} className="mx-auto mb-4 h-40 rounded-xl object-contain" />
          )}

          {/* AGC: Prize value */}
          {data.prizeValue && (
            <p className="mb-4 text-center text-2xl font-black text-green-400">
              {formatCurrency(data.prizeValue)}
            </p>
          )}

          {/* AGC: Info box with how-it-works and instagram note */}
          <div className="mb-4 rounded-xl bg-gray-800 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
              <p className="text-sm text-gray-300">{t('sorteio.modal.entryHowItWorks')}</p>
            </div>
            <div className="flex items-start gap-3">
              <Instagram className="mt-0.5 h-5 w-5 flex-shrink-0 text-pink-400" />
              <p className="text-sm text-gray-300">{t('sorteio.modal.entryInstagramNote')}</p>
            </div>
          </div>

          {/* AGC: Days left countdown */}
          <p className="mb-5 text-center text-sm text-gray-400">
            {t('sorteio.page.daysLeft', { days: daysLeft })}
          </p>

          {/* AGC: CTA button to navigate to tasks */}
          <button
            onClick={onGoToTasks}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-yellow-500 px-4 py-3 font-semibold text-black transition-colors hover:bg-yellow-400 active:bg-yellow-600"
          >
            {t('sorteio.modal.entryGoToTasks')}
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default SorteioEntryModal
